import { useState, useEffect } from 'react'
import { PageHeader, Modal, SearchInput, EmptyState } from '../components/UI'
import { get, post, put, del } from '../utils/api'
import { formatCurrency } from '../utils/helpers'
import { IconToolsKitchen2, IconPlus, IconEdit, IconTrash } from '@tabler/icons-react'

export default function CateringPage() {
  const [categories, setCategories] = useState([])
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('items')
  const [search, setSearch] = useState('')
  const [catFilter, setCatFilter] = useState('all')
  const [showCat, setShowCat] = useState(false)
  const [showItem, setShowItem] = useState(false)
  const [editCat, setEditCat] = useState(null)
  const [editItem, setEditItem] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)

  const load = async () => {
    try {
      const [c, i] = await Promise.all([get('/catering/categories'), get('/catering/items')])
      setCategories(c); setItems(i)
    } catch {} finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const filtered = items.filter(i => {
    if (catFilter !== 'all' && String(i.category_id) !== catFilter) return false
    if (!search) return true
    return i.name.toLowerCase().includes(search.toLowerCase()) || (i.description || '').toLowerCase().includes(search.toLowerCase())
  })

  const saveCat = async () => {
    setSaving(true)
    try {
      if (editCat) await put(`/catering/categories/${editCat.id}`, form)
      else await post('/catering/categories', form)
      setShowCat(false); setEditCat(null); setForm({}); load()
    } catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const saveItem = async () => {
    setSaving(true)
    try {
      if (editItem) await put(`/catering/items/${editItem.id}`, form)
      else await post('/catering/items', form)
      setShowItem(false); setEditItem(null); setForm({}); load()
    } catch (err) { alert(err.message) } finally { setSaving(false) }
  }

  const deleteCat = async (id) => { if (confirm('Delete this category and all its items?')) { await del(`/catering/categories/${id}`); load() } }
  const deleteItem = async (id) => { if (confirm('Delete this menu item?')) { await del(`/catering/items/${id}`); load() } }

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <>
      <PageHeader title="Catering & Menus" subtitle="Manage menu packages and pricing for events"
        actions={<>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setForm({}); setEditCat(null); setShowCat(true) }}><IconPlus size={16} className="me-1" />Category</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setForm({}); setEditItem(null); setShowItem(true) }}><IconPlus size={16} className="me-1" />Menu Item</button>
        </>} />

      <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
        <SearchInput value={search} onChange={setSearch} placeholder="Search menus..." />
        <select className="form-select form-select-sm" style={{ maxWidth: 200 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
      </div>

      {/* Categories Summary */}
      <div className="row row-cards mb-4">
        {categories.map(c => {
          const catItems = items.filter(i => i.category_id === c.id)
          const avgPrice = catItems.length ? catItems.reduce((s, i) => s + parseFloat(i.price_per_person || 0), 0) / catItems.length : 0
          return (
            <div key={c.id} className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold">{c.name}</div>
                      <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{catItems.length} items · Avg {formatCurrency(avgPrice)}/pp</div>
                    </div>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={() => { setEditCat(c); setForm({ ...c }); setShowCat(true) }}><IconEdit size={14} /></button>
                      <button className="btn btn-ghost-danger btn-icon btn-sm" onClick={() => deleteCat(c.id)}><IconTrash size={14} /></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Items Table */}
      {filtered.length === 0 ? <EmptyState icon={IconToolsKitchen2} title="No menu items" message="Add categories first, then create menu items" /> : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-vcenter card-table table-hover">
              <thead><tr><th>Item</th><th>Category</th><th>Price/Person</th><th>Min Guests</th><th>Dietary</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>{filtered.map(item => (
                <tr key={item.id}>
                  <td>
                    <div className="fw-medium">{item.name}</div>
                    {item.description && <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{item.description}</div>}
                  </td>
                  <td><span className="badge bg-blue-lt">{item.category_name}</span></td>
                  <td className="fw-medium">{formatCurrency(item.price_per_person)}</td>
                  <td>{item.min_guests || '---'}</td>
                  <td>{item.dietary_tags ? item.dietary_tags.split(',').map(t => <span key={t} className="badge bg-green-lt me-1">{t.trim()}</span>) : '---'}</td>
                  <td><span className={`badge bg-${item.is_active ? 'green' : 'red'}-lt`}>{item.is_active ? 'Yes' : 'No'}</span></td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-secondary btn-icon btn-sm" onClick={() => { setEditItem(item); setForm({ ...item }); setShowItem(true) }}><IconEdit size={16} /></button>
                      <button className="btn btn-ghost-danger btn-icon btn-sm" onClick={() => deleteItem(item.id)}><IconTrash size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Modal */}
      <Modal open={showCat} onClose={() => setShowCat(false)} title={editCat ? 'Edit Category' : 'Add Category'}>
        <div className="mb-3"><label className="form-label">Name</label><input className="form-control" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Breakfast Buffets" /></div>
        <div className="mb-3"><label className="form-label">Description</label><textarea className="form-control" rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
        <div className="mb-3"><label className="form-label">Sort Order</label><input type="number" className="form-control" value={form.sort_order || 0} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) })} /></div>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn" onClick={() => setShowCat(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={saveCat}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>

      {/* Item Modal */}
      <Modal open={showItem} onClose={() => setShowItem(false)} title={editItem ? 'Edit Menu Item' : 'Add Menu Item'} wide>
        <div className="row g-3">
          <div className="col-md-6"><label className="form-label">Name</label><input className="form-control" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Continental Breakfast" /></div>
          <div className="col-md-6"><label className="form-label">Category</label>
            <select className="form-select" value={form.category_id || ''} onChange={e => setForm({ ...form, category_id: parseInt(e.target.value) })}>
              <option value="">Select...</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select></div>
          <div className="col-12"><label className="form-label">Description</label><textarea className="form-control" rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div className="col-md-4"><label className="form-label">Price per Person</label><input type="number" step="0.01" className="form-control" value={form.price_per_person || ''} onChange={e => setForm({ ...form, price_per_person: e.target.value })} /></div>
          <div className="col-md-4"><label className="form-label">Min Guests</label><input type="number" className="form-control" value={form.min_guests || ''} onChange={e => setForm({ ...form, min_guests: e.target.value })} /></div>
          <div className="col-md-4"><label className="form-label">Dietary Tags</label><input className="form-control" value={form.dietary_tags || ''} onChange={e => setForm({ ...form, dietary_tags: e.target.value })} placeholder="GF, V, Vegan" /></div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn" onClick={() => setShowItem(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={saveItem}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </Modal>
    </>
  )
}
